import { Operator } from '../model/operator';
import { RequestCriteria } from '../model/request-criteria';
import * as moment from 'moment';
import { isString, isArray, every, isNumber, chain, get, cloneDeep, isPlainObject } from 'lodash';
import { EMPTY_NUMBER_VALUE } from 'src/app/common/const';
import { Sort, SortDirection } from '../model/transformations';
import { TranslateService } from '@ngx-translate/core';
import { FieldType } from 'src/app/settings/model/field-type';
import { Coordinate } from 'tsgeo/Coordinate';
import { Polygon } from 'tsgeo/Polygon';
import { Transform } from 'class-transformer';
import { SortBy } from '../model/data-source';
import { toVisibleDateTimeFormat } from './date-util';

const POSSIBLE_HASH_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function filterData<T>(criteria: RequestCriteria, data: T[]): T[] {
  if (criteria == null)
    return data;

  const criteriaValueLowerCase = toLowerCaseString(criteria.value);
  const isAllArray = every(data.map(element => element[criteria.fieldName]), isArray);

  switch (criteria.operator) {
    case Operator.equals:
      return data.filter(element => matchesEquals(element[criteria.fieldName], criteria.value));
    case Operator.inList: {
      // Pusta lista wybranych wartości to świadomy wybór "nic", a nie brak filtru - brak filtru
      // nie tworzy w ogóle kryterium (patrz ColumnFilterService).
      // Dopasowanie idzie po zbiorze, a nie przez przejście listy dla każdego wiersza: przy
      // setkach wybranych wartości i dziesiątkach tysięcy wierszy to różnica rzędu wielkości.
      const allowed = new Set((criteria.values ?? []).map(toComparableKey));
      if (!allowed.size && !criteria.includeEmpty) return [];
      return data.filter(element => {
        const value = element[criteria.fieldName];
        if (criteria.includeEmpty && isEmptyValue(value)) return true;
        if (!allowed.size) return false;
        if (isArray(value)) return value.some(item => allowed.has(toComparableKey(item)));
        if (isPlainObject(value)) return Object.keys(value).some(key => allowed.has(key));
        return allowed.has(toComparableKey(value)) && checkEmptyNumber(value);
      });
    }
    case Operator.iEquals:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item == criteriaValueLowerCase)
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName]) == criteriaValueLowerCase);
    case Operator.notEqual:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        return value != criteria.value && checkEmptyNumber(value);
      });
    case Operator.iNotEqual:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item !== criteriaValueLowerCase)
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName]) !== criteriaValueLowerCase);
    case Operator.greaterThan:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        return value > criteria.value && checkEmptyNumber(value);
      });
    case Operator.greaterOrEqual:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        return value >= criteria.value && checkEmptyNumber(value);
      });
    case Operator.lessThan:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        return value < criteria.value && checkEmptyNumber(value);
      });
    case Operator.lessOrEqual:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        return value <= criteria.value && checkEmptyNumber(value);
      });
    case Operator.isNull:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        const result = value === null || value === undefined || checkEmptyNumber(value, true);
        if (!result && (isString(value) || isArray(value)))
          return !value?.length;
        return result;
      });
    case Operator.notNull:
      return data.filter(element => {
        const value = element[criteria.fieldName];
        const result = value !== null && value !== undefined && checkEmptyNumber(value);
        if (result && (isString(value) || isArray(value)))
          return value?.length;
        return result;
      });
    case Operator.contains:
      return criteria.fieldName == FieldType.AREA ?
        filterByArea(criteria, data, true) : data.filter(element => element[criteria.fieldName].includes(criteria.value));
    case Operator.notContains:
      return criteria.fieldName == FieldType.AREA ?
        filterByArea(criteria, data, false) : data.filter(element => element[criteria.fieldName].indexOf(criteria.value) === -1);
    case Operator.startsWith:
      return data.filter(element => element[criteria.fieldName].startsWith(criteria.value));
    case Operator.endsWith:
      return data.filter(element => element[criteria.fieldName].endsWith(criteria.value));
    case Operator.iContains:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item?.includes(criteriaValueLowerCase))
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName])?.includes(criteriaValueLowerCase));
    case Operator.iNotContains:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item?.indexOf(criteriaValueLowerCase) === -1)
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName])?.indexOf(criteriaValueLowerCase) === -1);
    case Operator.iStartsWith:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item?.startsWith(criteriaValueLowerCase))
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName])?.startsWith(criteriaValueLowerCase));
    case Operator.iEndsWith:
      return isAllArray ?
        data.filter(element =>
          toLowerCaseString(element[criteria.fieldName]).some((item: string) => item?.endsWith(criteriaValueLowerCase))
        ) : data.filter(element => toLowerCaseString(element[criteria.fieldName])?.endsWith(criteriaValueLowerCase));
    case Operator.between:
      return data.filter(element => {
        const elementValue = element[criteria.fieldName];
        if (elementValue)
          return moment(elementValue).isBetween(moment(criteria.start), moment(criteria.end));
        return false;
      });
    case Operator.betweenInclusive:
      return data.filter(element => {
        const elementValue = element[criteria.fieldName];
        if (elementValue) {
          const momentValue = moment(elementValue);
          return momentValue.isSameOrAfter(moment(criteria.start)) && momentValue.isSameOrBefore(moment(criteria.end));
        }
        return false;
      });
    case Operator.iContainsCustom:
      return data.filter(element => {
        const elementValue = element[criteria.fieldName];
        if (criteria.fieldName == 'timetableDeviation' && (elementValue !== null && elementValue !== undefined)) {
          const absolute = Math.abs(elementValue as number);
          return toLowerCaseString(
            (element[criteria.fieldName] < 0 ? '-' : '') + moment.utc(moment.duration(absolute, 'seconds').asMilliseconds()).toTime(true))
            ?.includes(criteriaValueLowerCase);
        } else if (criteria.fieldName == 'speed' && (elementValue !== null && elementValue !== undefined)) {
          return toLowerCaseString(Math.round(element[criteria.fieldName] * 3.6))?.includes(criteriaValueLowerCase);
        }
        else
          return toLowerCaseString(element[criteria.fieldName])?.includes(criteriaValueLowerCase);
      });
    case Operator.and:
      if (criteria.criteria?.length) {
        return criteria.criteria.reduce((acc, innerCriteria) => filterData(innerCriteria, acc), data);
      }
      return data;
    case Operator.or:
      if (criteria.criteria?.length) {
        const filters = criteria.criteria.map(innerCriteria => innerCriteria.value);
        const resultData = filterAnyMatch(criteria.criteria[0], filters, data);
        return resultData;
      }
      return data;
    default:
      console.warn('Unsupported operator', criteria.operator);
      return data;
  }
}

// Klucz do porównań zbiorowych: wartości filtra przychodzą jako tekst (klucze opcji), a w wierszach
// bywają liczbami - luźne porównanie z equals sprowadzamy tu do wspólnej postaci tekstowej.
function toComparableKey(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

// "Bez wartości" na liście filtra: pusto to brak wartości, pusty tekst i puste zestawy - tak samo
// dla kolumny tekstowej, tablicowej (np. uchybienia odjazdu) i mapy.
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

function matchesEquals(value: unknown, expected: unknown): boolean {
  // Multi-value fields (e.g. a departure's violation ids) store an array on the row -
  // "equals" there means "is one of the selected values", not literal array equality.
  if (isArray(value)) return value.some(v => v == expected);
  // Some are keyed maps instead (e.g. a departure's claims: { [violationId]: status }) -
  // "equals" there means "has this key", regardless of its value.
  if (isPlainObject(value)) return Object.keys(value).some(k => k == expected);
  return value == expected && checkEmptyNumber(value);
}

function filterAnyMatch<T>(criteria: RequestCriteria, filters: string[], data: T[]): T[] {
  switch (criteria.operator) {
    case Operator.equals:
      return data.filter(element => filters.includes(element[criteria.fieldName]));
    case Operator.iEquals:
      const lowerCaseFilters = filters.map(filter => toLowerCaseString(filter));
      return data.filter(element => {
        const elementLowerCase = toLowerCaseString(element[criteria.fieldName]);
        return lowerCaseFilters.some(filter => filter == elementLowerCase);
      });
    default:
      console.warn('Unsupported operator', criteria.operator);
      return data;
  }
}

function filterByArea<T>(criteria: RequestCriteria, data: T[], contains: boolean): T[] {
  // extract points from string like POLYGON((lng1 lat1, lng2 lat2, ....))
  const pointStrings = criteria.value.match(/(\d+\.\d+ \d+\.\d+)/g);
  const geofence = new Polygon();
  pointStrings.forEach(pointString => {
    const values = pointString.split(' ');
    if (values.length == 2) {
      geofence.addPoint(new Coordinate(+values[1], +values[0]));
    } else {
      console.warn('Invalid points string to filter by area', pointString);
    }
  });
  return data.filter(element => {
    const lat = get(element, 'latitude') || get(element, 'lat');
    const lng = get(element, 'longitude') || get(element, 'lng');
    const point = new Coordinate(+lat, +lng);
    const containsPoint = geofence.contains(point);
    return contains ? containsPoint : !containsPoint;
  });
}

export function groupData<T>(data: T[], groupBy: string, sort: Sort[], translate: TranslateService): GroupedData<T>[] {
  const groupped: GroupedData<T>[] = chain(data)
    .groupBy(groupBy)
    .map((value, key) => Object.assign(new GroupedData<T>(), { data: value, groupName: getGroupName(key, translate) }))
    .value();
  const groupedSort = sort.find(s => s.active === groupBy);
  if (groupedSort) {
    sort.push({
      active: 'groupName',
      direction: groupedSort.direction,
      // Nazwa grupy to surowa wartość kolumny, więc jej porządek ustawia też kolejność nagłówków grup.
      valueOrder: groupedSort.valueOrder
    });
  }
  return sortData(groupped, sort);
}

function getGroupName(key: string, translate: TranslateService): string {
  if (key && key?.trim().length && key !== 'undefined' && key !== 'null')
    return key;
  else
    return translate.instant('noValues');
}

// Sortery powstają wprost z Sort, bo SortBy niesie tylko pole i kierunek i gubi Sort.valueOrder.
// Wpisy bez pola albo bez kierunku pomijamy, żeby jeden zepsuty nie wywrócił wystawienia danych.
export function sortData<T>(data: T[], sort: Sort[]): T[] {
  const sorters = createSorters(sort.filter(s => s?.active && s?.direction), data);
  if (sorters.length)
    return data.sort(fieldSorterOptimized(sorters));
  return data;
}

/**
 * Applies a RequestCriteria/SortBy pair (as used by server-side fetches) to an in-memory
 * array - mirrors what MaterialTableDataSource.transformAndPublishData does for on-screen
 * websocket-backed tables, so client-side loaders can honor the same filters/sort on export.
 */
export function applyCriteriaAndSorters<T>(data: T[], filters?: RequestCriteria, sorters?: SortBy[]): T[] {
  let result = cloneDeep(data);
  if (sorters?.length) {
    const sort: Sort[] = sorters.map(s => ({ active: s.property, direction: s.direction as SortDirection }));
    result = sortData(result, sort);
  }
  filters?.criteria?.forEach(criteria => result = filterData(criteria, result));
  return result;
}

interface Sorters {
  direction: number;
  field: string;
  isDataString?: boolean;
  valueOrder?: string[];
}

function createSorters<T>(sort: Sort[], data: T[]): Sorters[] {
  const sorters: Sorters[] = [];
  sort.forEach(field => {
    const dir = field.direction === SortDirection.DESC ? -1 : 1;
    sorters.push({ direction: dir, field: field.active, valueOrder: field.valueOrder });
  });
  for (const sorter of sorters) {
    sorter.isDataString = every(data.map(item => get(item, sorter.field)), isString);
  }
  return sorters;
}

function fieldSorterOptimized<T>(sorters: Sorters[]): (a: T, b: T) => number {
  return (one: T, two: T) => {
    for (const sorter of sorters) {
      const field = sorter.field;
      const direction = sorter.direction;
      // Kolumna z własnym porządkiem wartości (Sort.valueOrder): porównujemy pozycje na liście, nie same
      // wartości. Wartość nieznana (indexOf == -1) trafia przed wszystkie znane, jak puste niżej.
      if (sorter.valueOrder) {
        const result = (sorter.valueOrder.indexOf(get(one, field)) - sorter.valueOrder.indexOf(get(two, field))) * direction;
        if (result !== 0)
          return result;
      } else if (sorter.isDataString) {
        let result: number;
        if (direction > 0)
          result = get(one, field).localeCompare(get(two, field), undefined, { numeric: true, sensitivity: 'base' });
        else if (direction < 0)
          result = get(two, field).localeCompare(get(one, field), undefined, { numeric: true, sensitivity: 'base' });
        if (result !== 0)
          return result;
      } else {
        if (get(one, field) === undefined || get(one, field) === null) return -direction;
        if (get(two, field) === undefined || get(two, field) === null) return direction;
        if (get(one, field) > get(two, field)) return direction;
        if (get(one, field) < get(two, field)) return -(direction);
      }
    }
    return 0;
  };
}

// TODO consider using library like hashcode to generate hash code for object
export function generateKey(keyObject: any): string {
  let key = '';
  Object.keys(keyObject).sort().forEach(objectKey => {
    key += `${objectKey}:${keyObject[objectKey]};`;
  });
  return key;
}

export function generateRandomHash(length: number = 10): string {
  let hash = '';
  for (let i = 0; i < length; i++)
    hash += POSSIBLE_HASH_CHARS.charAt(Math.floor(Math.random() * POSSIBLE_HASH_CHARS.length));
  return hash;
}

function toLowerCaseString(value: any): any {
  if (isArray(value) && every(value, isString))
    return value.map(element => element.toLowerCase());

  // Date's own toString() ("Fri Feb 02 2024...") has no relation to what's actually rendered in
  // the cell (DD-MM-YYYY), so text-containment search against it would never match what a user
  // sees or types - use the same visible format the table displays instead.
  if (value instanceof Date)
    return toVisibleDateTimeFormat(value).toLowerCase();

  if (!isString(value))
    return value?.toString()?.toLowerCase();
  return value.toLowerCase();
}

function checkEmptyNumber(value: any, isEmpty: boolean = false): boolean {
  if (isNumber(value))
    return isEmpty ? value == EMPTY_NUMBER_VALUE : value != EMPTY_NUMBER_VALUE;
  else
    return !isEmpty;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function Default(defaultValue: unknown): PropertyDecorator {
  return Transform((value: unknown) => value ?? cloneDeep(defaultValue));
}

export class GroupedData<T> {
  data: T[];
  groupName: string;
}
